import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface AttentionBorderProps {
  /** When true (default), applies the moving monochrome streak border ring. */
  active?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Wraps its children in a relative container that paints a slow monochrome
 * sweep along the 1.5px border ring via the `.attention-border` utility in
 * globals.css.
 *
 * Design constraints (docs/07 §3.1 / §3.6):
 * - Monochrome grays only — no accent, no glow bleed outward.
 * - The ring sits flush on the card border radius (border-radius: inherit in
 *   the ::before pseudo-element).
 * - Reduced-motion: animation is disabled; a static, slightly-darker ring is
 *   shown instead.
 *
 * Usage:
 *   <AttentionBorder>
 *     <Card …>…</Card>
 *   </AttentionBorder>
 *
 *   <AttentionBorder active={false}>   {/* no effect *\/}
 *     <Card …>…</Card>
 *   </AttentionBorder>
 */
export function AttentionBorder({ active = true, className, children }: AttentionBorderProps) {
  return (
    <div className={cn('rounded-xl', active && 'attention-border', className)}>{children}</div>
  );
}
